import React, { useState } from "react";
import api, { money } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const FIELDS = [
  ["customers", "Customers"], ["rice_kg_per_customer", "Rice kg / customer"],
  ["rice_contribution_per_kg", "Rice contribution / kg (₹)"], ["basket_per_customer", "Grains/Pulses basket / customer (₹)"],
  ["basket_margin_pct", "Grains/Pulses margin %"], ["old_rice_kg_per_customer", "Old rice kg / customer"],
  ["ricepoints_per_kg", "RicePoints / kg (₹)"], ["operating_cost_per_customer", "Operating cost / customer (₹)"],
  ["old_rice_recovery_per_kg", "Old rice recovery / kg (₹)"], ["redemption_rate_pct", "Est. redemption rate %"],
];

const DEFAULTS = {
  customers: 200, rice_kg_per_customer: 25, rice_contribution_per_kg: 10, basket_per_customer: 1000,
  basket_margin_pct: 20, old_rice_kg_per_customer: 12.5, ricepoints_per_kg: 10,
  operating_cost_per_customer: 40, old_rice_recovery_per_kg: 0, redemption_rate_pct: 100,
};

export default function AdminSimulator() {
  const [inputs, setInputs] = useState(DEFAULTS);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setInputs({ ...inputs, [k]: Number(e.target.value) });

  const run = async () => {
    const { data } = await api.post("/admin/simulator", inputs);
    setResult(data);
  };
  React.useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  return (
    <AdminLayout title="RicePoints Business Simulator">
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-5 lg:col-span-1">
          <div className="font-heading font-bold mb-3">Inputs</div>
          <div className="grid gap-3">
            {FIELDS.map(([k, label]) => (
              <div key={k}><Label className="text-xs">{label}</Label>
                <Input type="number" step="0.1" value={inputs[k]} onChange={set(k)} data-testid={`sim-${k}`} className="mt-1 h-8" /></div>
            ))}
          </div>
          <Button className="w-full mt-4" onClick={run} data-testid="sim-run-btn">Recalculate</Button>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {result && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="sim-outputs">
                {[
                  ["Monthly revenue", result.base.monthly_revenue], ["Rice contribution", result.base.rice_contribution],
                  ["Grain/Pulse contribution", result.base.grain_pulse_contribution], ["Points issued", result.base.points_issued],
                  ["Est. points redeemed", result.base.points_redeemed], ["Old rice recovery", result.base.old_rice_recovery],
                  ["Operating expenses", result.base.operating_expenses], ["Operating profit", result.base.operating_profit],
                  ["Annual profit", result.base.annual_profit],
                ].map(([l, v]) => (
                  <div key={l} className="bg-card border border-border rounded-lg p-3">
                    <div className="text-xs text-muted-foreground">{l}</div>
                    <div className={`font-mono-points font-bold text-lg ${l.includes("profit") ? (v >= 0 ? "text-[hsl(var(--secondary))]" : "text-destructive") : ""}`}>{money(v)}</div>
                  </div>
                ))}
              </div>

              <div className="bg-card border border-border rounded-lg p-5">
                <div className="font-heading font-bold mb-2">Profit across scenarios</div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={result.scenarios}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="customers" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${v / 1000}k`} />
                    <Tooltip formatter={(v) => money(v)} />
                    <Line type="monotone" dataKey="operating_profit" stroke="#D95D39" strokeWidth={2} name="Monthly profit" />
                  </LineChart>
                </ResponsiveContainer>
                <div className="overflow-x-auto mt-3">
                  <table className="w-full text-xs">
                    <thead className="text-left text-muted-foreground"><tr><th className="p-2">Customers</th><th className="p-2 text-right">Revenue</th><th className="p-2 text-right">Points issued</th><th className="p-2 text-right">Op. profit</th><th className="p-2 text-right">Annual</th></tr></thead>
                    <tbody>
                      {result.scenarios.map((s) => (
                        <tr key={s.customers} className="border-t border-border font-mono-points" data-testid={`sim-scenario-${s.customers}`}>
                          <td className="p-2 font-semibold">{s.customers.toLocaleString("en-IN")}</td>
                          <td className="p-2 text-right">{money(s.monthly_revenue)}</td>
                          <td className="p-2 text-right">{money(s.points_issued)}</td>
                          <td className="p-2 text-right">{money(s.operating_profit)}</td>
                          <td className="p-2 text-right font-semibold">{money(s.annual_profit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
